/* eslint-disable no-promise-executor-return, no-underscore-dangle */
// https://gist.github.com/AndrewIngram/a77afd0c2d7a7bba52913d7a333d88bf
// https://nextjs.org/blog/next-7

import { graphql, fetchQuery } from 'react-relay';
import wasConf from 'was.config.js';
import Page from '@/components/pages/search.js';
import {
  initEnvironment,
} from '@/relay/index.js';
import ai from '@/helpers/ai.js';
import cachedSSR from '@/helpers/ssrQueryCache.js';

function replaceObjBy(tab, matcher, newValue) {
  const index = tab.findIndex(matcher);
  if (index !== -1) {
    tab.splice(index, 1, ...newValue);
  }
  return tab;
}

const queryLocation = graphql`
  query LocationFiltersQuery {
    districts: geoGetDistricts {
      center
      slug
      points
    }
  }
`;

const queryPlace = graphql`
  query LocationPlaceFromUrlServerSideQuery($pattern: String!){
    res: getPlacesByPattern(pattern: $pattern, what: "useBDD") {
      title
      place_id
    }
  }
`;
const queryPlaceId = graphql`
  query LocationPlaceIDFromUrlServerSideQuery($place_id: String){
    res : geoGetByPlaceId(place_id: $place_id) {
      location {
        lat
        lng
        viewport {
          northeast {
            lat
            lng
          }
          southwest {
            lat
            lng
          }
        }
      }
    }
  }
`;

const querySpace = graphql`
  query LocationSearchSpaceByParamsQuery($params: SearchSpaceParams, $lang: String){
    results: searchSpaceByParams(params: $params) {
      resume
      spaces {
        ...spaceResultSearchFragment @arguments(lang: $lang)
      }
    }
  }
`;

export async function getServerSideProps(context) {
  const locale = context?.locale || 'en';
  if (!context?.params?.location) return { props: { statusCode: 404 } };
  if ('page' in context.query && parseInt(context.query.page, 10) < 0) {
    return { props: { statusCode: 404 } };
  }
  // console.dir(wasConf, { depth: null });
  const environment = initEnvironment();

  // WEA-173 — TTFB : les données quasi statiques partent EN PARALLÈLE dès l'entrée (elles ne
  // dépendent pas du lieu) et passent par le cache mémoire (helpers/ssrQueryCache.js). Elles
  // seront attendues plus bas, après la chaîne place → placeId → grille. Chaque fetch de
  // cache-miss crée SON environnement Relay : leurs records ne polluent pas queryRecords
  // (seuls la grille — et la géo du lieu en cache-miss — passent par `environment`).
  const districtsPromise = cachedSSR('districts', 10 * 60e3, () => {
    const environmentGeo = initEnvironment();
    return new Promise((resolve) => fetchQuery(environmentGeo, queryLocation, {}, { force: true }).subscribe({
      next: (dataRes) => {
        resolve(dataRes);
      },
      error: (error1) => {
        console.log('error1', error1);
        resolve(null);
      },
    }));
  }, (v) => !!v?.districts?.length);
  const tradPromise = cachedSSR(`trad:${locale}`, 10 * 60e3, async () => {
    const i18n = await import('../../i18n.js');
    return i18n.getByPages({ routes: ['*', '/s/[location]'], locale });
  }, (v) => !!v && !!Object.keys(v).length);
  const aiPromise = cachedSSR('aiconf', 10 * 60e3, () => ai({ environment: initEnvironment() }), (v) => !!v);

  if (context?.query?.riders === 'null') delete context.query.riders;

  let variables = { ...context.query };
  const location = context?.params?.location;
  let urlCoord = { ...(variables?.lat ? { lat: variables?.lat } : {}) };
  urlCoord = { ...urlCoord, ...(variables?.lng ? { lng: variables?.lng } : {}) };

  // WEA-173 — la géo d'un lieu est stable : cache 1 h par slug (un slug inconnu → résultat
  // vide, non caché, donc les 404 restent réévalués à chaque requête).
  const res = await cachedSSR(`place:${location}`, 60 * 60e3, () => new Promise((resolve) => fetchQuery(environment, queryPlace, {
    pattern: location,
  }, { force: true }).subscribe({
    next: (dataRes) => {
      resolve(dataRes);
    },
    error: (error1) => {
      console.log('error1', error1);
      resolve(null);
    },
  })), (v) => !!v?.res?.length);

  if (!res?.res?.length) return { props: { statusCode: 404 } };

  const resPlaceId = await cachedSSR(`placeid:${res?.res[0].place_id}`, 60 * 60e3, () => new Promise((resolve) => fetchQuery(environment, queryPlaceId, {
    place_id: res?.res[0].place_id,
  }, { force: true }).subscribe({
    next: (dataRes) => {
      resolve(dataRes);
    },
    error: (error1) => {
      console.log('error1', error1);
      resolve(null);
    },
  })), (v) => !!v?.res?.location?.viewport);

  if (!resPlaceId?.res?.location?.viewport) return { props: { statusCode: 404 } };

  let spaces = {};
  // WEA-176 — annonce au navigateur qu'on sait lire la largeur de viewport en Client Hint :
  // Chrome/Edge l'enverront (Sec-CH-Viewport-Width) à partir de la requête suivante.
  context.res?.setHeader?.('Accept-CH', 'Sec-CH-Viewport-Width');
  // WEA-176 — la grille SSR couvre désormais AUSSI les URLs à paramètres purement
  // géographiques (carte bougée, lien partagé, bookmark : hub/title/lat/lng/zoom).
  // Ces URLs recevaient une coquille vide : l'image LCP attendait boot JS → init carte →
  // recherche (~20 s mesurées en prod). Le serveur sait pourtant tout : lat/lng = le
  // centre, le zoom = la taille de la zone. Les URLs avec de VRAIS filtres (dates, prix,
  // riders, mots-clés…) restent client-side — le serveur ne devine pas un filtre.
  // Centre : lat/lng de l'URL s'ils sont plausibles, sinon le centre-ville (URL propre).
  const urlLat = parseFloat(context.query.lat);
  const urlLng = parseFloat(context.query.lng);
  const hasGeo = Number.isFinite(urlLat) && Math.abs(urlLat) <= 90
    && Number.isFinite(urlLng) && Math.abs(urlLng) <= 180;
  const pageNum = parseInt(context.query.page, 10) || 1;
  // WEA-176 — CAP de profondeur sur les URLs paramétrées : le rectangle serveur est une
  // approximation de la carte du visiteur, et une approximation DIVERGE avec la profondeur
  // (même top-24 en page 1, mais au rang 600 un lieu de plus dans le rectangle décale
  // toute la fenêtre — « le premier résultat disparaît au refresh », constaté par Hieu
  // p.26 ; 1re page divergente mesurée : Lyon 3, Paris 8). Au-delà de la page 2 (zone
  // mesurée sûre), coquille pré-176 : le client fetch avec ses bounds EXACTES, donc
  // refresh == clic par construction. Les URLs propres (?page=N sans lat/lng — ce que
  // crawle Google via les liens de pagination relatifs) gardent la grille SSR à toute
  // profondeur : zéro changement SEO.
  if (!Object.keys(context.query || { toto: 1 }).filter((q) => !['location', 'page', 'category', 'hub', 'title', 'lat', 'lng', 'zoom'].includes(q)).length
    && (!hasGeo || pageNum <= 2)) {
    const pageFrom = (pageNum - 1) * 24;
    const center = hasGeo
      ? [urlLat, urlLng]
      : [resPlaceId?.res.location.lat, resPlaceId?.res.location.lng];
    // WEA-176 — le serveur calcule LE MÊME RECTANGLE que la carte cliente (fini le cercle :
    // à Lyon, une villa périphérique était dans le rectangle haut de la carte, ±12 km
    // verticalement, mais hors d'un cercle de ~10 km — la densité parisienne masquait cet
    // écart de forme, constaté par Hieu). Carte canonique DESKTOP : 534 × 964 px —
    // dimensions RÉTRO-CALCULÉES depuis les bounds réelles d'une carte desktop à z12,
    // qu'elles reproduisent à la 4e décimale. Résolution Web-Mercator :
    // 156543,03 × cos(lat) / 2^z m/px. Zoom absent (URL propre, recherche homepage) ou
    // farfelu → zoom 12, le défaut de la carte (components/search/map.js) — l'ancien 6 km
    // fixe de WEA-169 divergeait dès la première pagination.
    // Sur MOBILE la carte n'a pas cette forme : elle emplit le viewport (390 × 781 px
    // mesurés sur 390 × 787, soit hauteur ≈ 2 × largeur) et le canonique desktop donnait,
    // mesuré : Paris 22/23, Lyon 19/23 entre l'écran d'arrivée et le retour page 1.
    // Détection : Sec-CH-UA-Mobile (Client Hint bas-entropie, envoyé d'office par
    // Chrome/Edge), sinon l'User-Agent (Safari n'envoie AUCUN Client Hint — `Mobi` couvre
    // Chrome Android « Mobile » et Safari iPhone « Mobile/15E148 »). Largeur affinée par
    // Sec-CH-Viewport-Width quand il est là (2e requête et suivantes, cf. Accept-CH plus
    // haut), bornée aux téléphones réels [320, 500] px. Le rectangle reste une
    // approximation du device dominant de chaque famille — la 1re interaction repasse sur
    // les bounds exactes du client.
    const chMobile = context.req?.headers?.['sec-ch-ua-mobile'];
    const isMobile = chMobile
      ? chMobile === '?1'
      : /iPhone|iPod|Mobi/i.test(context.req?.headers?.['user-agent'] || '');
    // Desktop avec hint : la colonne carte fait ~31 % de la largeur de fenêtre (mesuré
    // 1280→400, 1366→426, 1600→496, 1728→534) et hauteur ≈ 1,66 × largeur — modèle
    // départagé contre les tailles réelles (24/24 partout sauf Lyon 1280 : 23/24 ; le
    // canonique fixe donnait 21/24). Sans hint (Safari, 1re requête) : canonique 534×964.
    const chWidth = parseInt(context.req?.headers?.['sec-ch-viewport-width'], 10);
    let mapW = 534;
    let mapH = 964;
    if (isMobile) {
      mapW = Number.isFinite(chWidth) && chWidth >= 320 && chWidth <= 500 ? chWidth : 390;
      mapH = mapW * 2;
    } else if (Number.isFinite(chWidth) && chWidth >= 900 && chWidth <= 3900) {
      mapW = Math.round(0.31 * chWidth);
      mapH = Math.round(1.66 * mapW);
    }
    const zoom = parseInt(context.query.zoom, 10);
    const zoomEffectif = Number.isFinite(zoom) && zoom >= 3 && zoom <= 21 ? zoom : 12;
    const mParPx = (156543.03392 * Math.cos((center[0] * Math.PI) / 180)) / 2 ** zoomEffectif;
    const dLat = ((mapH / 2) * mParPx) / 110574;
    const dLng = ((mapW / 2) * mParPx) / (111320 * Math.cos((center[0] * Math.PI) / 180));
    const varQuerySpace = {
      category: 'production',
      limit: { from: pageFrom || 0, to: 24 },
      // WEA-169 — ce résultat n'est plus seulement le HTML pour les crawlers, c'est la
      // grille que le visiteur garde à l'écran jusqu'à sa 1re interaction.
      boundaries: [[center[0] - dLat, center[1] - dLng], [center[0] + dLat, center[1] + dLng]],
      zoom: zoomEffectif,
      front: true,
      lang: locale,
    };
    spaces = await new Promise((resolve) => fetchQuery(environment, querySpace, {
      category: 'production',
      lang: locale,
      params: varQuerySpace,
    }, { force: true }).subscribe({
      next: (dataRes) => {
        resolve(dataRes);
      },
      error: (error1) => {
        console.log('error1', error1);
        resolve({});
      },
    }));
  }

  // WEA-172 — chaque ref de fragment porte le MÊME RequestDescriptor (~8 Ko : texte + AST de
  // la requête) : sérialisé 24 fois dans __NEXT_DATA__, il pesait 184 des 832 Ko de page data.
  // On le met de côté UNE fois (`owner`) ; _app le raccroche à chaque item avant tout rendu
  // via inflateSsrSpaces (helpers/searchctx.js). La donnée des espaces, elle, vit déjà dans
  // queryRecords.
  if (spaces?.results?.spaces?.length) {
    spaces = {
      owner: spaces.results.spaces[0].__fragmentOwner,
      results: {
        ...spaces.results,
        spaces: spaces.results.spaces.map(({ __fragmentOwner, ...ref }) => ref),
      },
    };
  }

  if (!urlCoord?.lat) urlCoord.lat = resPlaceId?.res?.location?.lat;
  if (!urlCoord?.lng) urlCoord.lng = resPlaceId?.res?.location?.lng;

  urlCoord = {
    ...urlCoord,
    boundNElat: resPlaceId?.res?.location?.viewport?.northeast?.lat,
    boundNElng: resPlaceId?.res?.location?.viewport?.northeast?.lng,
    boundSWlat: resPlaceId?.res?.location?.viewport?.southwest?.lat,
    boundSWlng: resPlaceId?.res?.location?.viewport?.southwest?.lng,
  };

  if (!variables.category) variables.category = 'production';
  variables = {
    ...variables, title: res?.res[0].title || '', hub: location, ...urlCoord,
  };

  // WEA-173 — districts (env Relay séparé depuis WEA-172 : leurs 189 Ko de polygones ne
  // doublonnent pas queryRecords), traductions et conf IA ont été lancés en parallèle en
  // tête de fonction — on ne fait que les attendre ici.
  const [dataFromServer, tradFromServer, aiKWConf] = await Promise.all([districtsPromise, tradPromise, aiPromise]);
  const queryRecords = environment.getStore().getSource().toJSON();
  const localwasConf = structuredClone(wasConf);

  // console.log('dataFromServer', localwasConf?.searchFilterConf?.production?.onFilter, aiKWConf?.[locale]);
  if (localwasConf?.searchFilterConf?.production?.onFilter?.length && aiKWConf?.[locale]?.length) {
    const ailist = aiKWConf?.[locale];
    const confAI = ailist.reduce((acc, aiitem) => {
      // console.log('aiitem.slug', aiitem.slug);
      if (['piece', 'ambiences'].includes(aiitem.slug)) {
        acc.onScreen.push({
          id: `V84NzDG3uhjK2pKw28${aiitem.slug}`,
          typecomponent: 'bypattern',
          title: `search:filter.category.${aiitem.slug}`,
          slug: 'patternkeywords',
          defaultOptions: aiitem.content.map((contentitem) => ({
            label: contentitem,
            value: contentitem,
          })),
          icon: aiitem.slug,
          modal: true,
        });
      } else {
        acc.onFilter.push({
          id: `V84NzDG3uhjK2pKw28${aiitem.slug}`,
          typecomponent: 'list',
          title: `search:filter.category.${aiitem.slug}`,
          slug: 'patternkeywords',
          values: aiitem.content.map((contentitem) => ({
            slug: contentitem,
            title: contentitem,
          })),
        });
      }

      return acc;
    }, { onScreen: [], onFilter: [] });

    localwasConf.searchFilterConf.production.onScreen = replaceObjBy(
      localwasConf.searchFilterConf.production.onScreen,
      (objF) => objF.bereplacebyai,
      confAI.onScreen,
    );
    localwasConf.searchFilterConf.production.onFilter.push(...confAI.onFilter);
  }
  return {
    props: {
      locale,
      queryRecords,
      variables,
      statusCode: 200,
      componentPage: 'search',
      noFooter: true,
      spaces,
      dataFromServer,
      tradFromServer,
      initParams: variables,
      filters: localwasConf?.searchFilterConf,
    },
  };
}

// eslint-disable-next-line react/prop-types
export default function Search({
  variables, spaces, dataFromServer, initParams, filters,
}) {
  const {
    districts,
  } = dataFromServer || {};
  return (
    <Page {...{
      filters,
      districts,
      spaces,
      initParams,
    }}
    />
  );
}
